import { ApiClientError } from "../api/http.js";
import type { TeamRole } from "../api/teams.js";

export const ROLE_ORDER: ReadonlyArray<TeamRole> = ["owner", "admin", "member", "viewer"];

export const ROLE_LABEL: Record<TeamRole, string> = {
  owner: "Eigentümer",
  admin: "Admin",
  member: "Mitglied",
  viewer: "Lesend"
};

export const ROLE_DESCRIPTION: Record<TeamRole, string> = {
  owner: "Verwaltet alles, inklusive Löschen des Teams.",
  admin: "Verwaltet Mitglieder, Sammlungen und Einstellungen.",
  member: "Liest und bearbeitet Einträge.",
  viewer: "Liest Einträge, ändert sie aber nicht."
};

export const AUDIT_LABEL: Record<string, string> = {
  org_created: "Team erstellt",
  org_updated: "Team geändert",
  org_deleted: "Team gelöscht",
  member_added: "Mitglied hinzugefügt",
  member_updated: "Mitglied aktualisiert",
  member_role_changed: "Rolle geändert",
  member_removed: "Mitglied entfernt",
  member_left: "Mitglied hat das Team verlassen",
  collection_created: "Sammlung erstellt",
  collection_updated: "Sammlung umbenannt",
  collection_deleted: "Sammlung gelöscht",
  collection_opened: "Einträge einer Sammlung geladen",
  team_synced: "Team synchronisiert",
  item_created: "Eintrag angelegt",
  item_opened: "Eintrag geöffnet",
  item_updated: "Eintrag geändert",
  item_deleted: "Eintrag in den Papierkorb",
  item_restored: "Eintrag wiederhergestellt",
  orgkey_rotated: "Team-Schlüssel rotiert",
  invite_created: "Einladung erstellt",
  invite_revoked: "Einladung widerrufen",
  invite_accepted: "Einladung angenommen",
  recovery_used: "Wiederherstellung genutzt"
};

/** Read-only events. Every list/open writes one, so they drown out changes unless hidden. */
export const READ_AUDIT_ACTIONS: ReadonlySet<string> = new Set(["collection_opened", "item_opened", "team_synced"]);

export interface AuditGroup<T> {
  readonly first: T;
  readonly count: number;
}

/** Collapses consecutive events with the same action and actor into one row. Input order is kept. */
export function groupAuditEvents<T extends { readonly action: string; readonly actorId: string | null }>(
  events: ReadonlyArray<T>,
  includeReads: boolean
): AuditGroup<T>[] {
  const groups: AuditGroup<T>[] = [];
  for (const event of events) {
    if (!includeReads && READ_AUDIT_ACTIONS.has(event.action)) {
      continue;
    }
    const last = groups[groups.length - 1];
    if (last !== undefined && last.first.action === event.action && last.first.actorId === event.actorId) {
      groups[groups.length - 1] = { first: last.first, count: last.count + 1 };
    } else {
      groups.push({ first: event, count: 1 });
    }
  }
  return groups;
}

export function canManageTeam(role: TeamRole): boolean {
  return role === "owner" || role === "admin";
}

export function canWriteItems(role: TeamRole): boolean {
  return role === "owner" || role === "admin" || role === "member";
}

export function isOwnerRole(role: TeamRole): boolean {
  return role === "owner";
}

/** Roles the actor may hand out. Mirrors the API: only owners grant owner or admin. */
export function assignableRoles(actor: TeamRole): ReadonlyArray<TeamRole> {
  if (actor === "owner") {
    return ROLE_ORDER;
  }
  if (actor === "admin") {
    return ["member", "viewer"];
  }
  return [];
}

/** Maps API errors to German copy. Unknown errors get a generic message; no raw server text leaks through. */
/** The team requires 2FA and the caller has neither a verified TOTP nor a passkey. */
export function isTeamTwoFactorError(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 403 && err.message.includes("Two-factor");
}

export function describeTeamError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 401) {
      return "Sitzung abgelaufen. Bitte melde dich erneut an.";
    }
    if (err.status === 404) {
      return "Nicht gefunden. Vielleicht wurde es bereits entfernt.";
    }
    if (err.status === 429) {
      return "Zu viele Anfragen. Bitte warte einen Moment.";
    }
    if (isTeamTwoFactorError(err)) {
      return "Für dieses Team ist 2FA erforderlich. Richte 2FA in den Einstellungen ein.";
    }
    if (err.message.includes("last owner")) {
      return "Der letzte Eigentümer kann nicht entfernt oder herabgestuft werden.";
    }
    if (err.message.includes("Invite outdated")) {
      return "Diese Einladung ist ungültig, weil der Team-Schlüssel inzwischen erneuert wurde. Lass dich neu einladen.";
    }
    if (err.message.includes("Invite link not opened")) {
      return "Öffne zuerst den Einladungslink in diesem Konto.";
    }
    if (err.message.includes("Already a member")) {
      return "Du bist bereits Mitglied dieses Teams.";
    }
    if (err.message.includes("changed during rotation") || err.message.includes("Rotation conflicted")) {
      return "Einträge wurden gleichzeitig geändert. Bitte versuche es erneut.";
    }
    if (err.message.includes("Version mismatch")) {
      return "Der Eintrag wurde zwischenzeitlich geändert. Lade die Seite neu.";
    }
    if (err.message.includes("Read-only")) {
      return "Deine Rolle erlaubt keine Änderungen.";
    }
    if (err.status === 403) {
      return "Dafür fehlt dir die Berechtigung.";
    }
    if (err.status === 409) {
      return "Es gibt einen Konflikt. Lade die Seite neu und versuche es erneut.";
    }
  }
  if (err instanceof Error && err.message === "Der Tresor ist gesperrt.") {
    return err.message;
  }
  return "Aktion fehlgeschlagen. Bitte erneut versuchen.";
}
